import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  alpha,
} from "@mui/material";
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import NatureIcon from '@mui/icons-material/Nature';
import ForestIcon from '@mui/icons-material/Forest';
import TerrainIcon from '@mui/icons-material/Terrain';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import GridOnIcon from '@mui/icons-material/GridOn';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedIcon from '@mui/icons-material/Verified';
import LanguageIcon from '@mui/icons-material/Language';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import "../../i8n";
import { useTranslation } from "react-i18next";

const colors = {
  darkGreen: "#1B5E20",
  mediumGreen: "#2E7D32",
  lightGreen: "#4CAF50",
  paleGreen: "#E8F5E9",
  darkOrange: "#E65100",
  mediumOrange: "#F57C00",
  lightOrange: "#FF9800",
  paleOrange: "#FFF3E0",
  white: "#FFFFFF",
  darkText: "#1a1a1a",
  subText: "#4a4a4a",
  cardBg: "#FAFFFE",
  sectionBg: "#F1F8E9",
};

const agroforestryIcons = [
  <RecordVoiceOverIcon />,
  <LocalFloristIcon />,
  <TerrainIcon />,
  <NatureIcon />,
  <ForestIcon />,
];

const wayForwardIcons = [
  <GridOnIcon />,
  <MapIcon />,
  <DescriptionIcon />,
  <VerifiedIcon />,
  <LanguageIcon />,
  <MergeTypeIcon />,
];

function SectionHeader({ icon, label, title, subtitle }) {
  return (
    <Box sx={{ textAlign: "center", mb: 5 }}>
      <Chip
        icon={icon}
        label={label}
        sx={{
          bgcolor: colors.paleGreen,
          color: colors.darkGreen,
          fontWeight: 700,
          fontSize: "0.8rem",
          py: 2.5,
          px: 1,
          mb: 2,
          "& .MuiChip-icon": { color: colors.darkGreen },
        }}
      />
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: colors.darkGreen,
          fontSize: { xs: "1.8rem", md: "2.5rem" },
          lineHeight: 1.2,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            color: colors.subText,
            maxWidth: 700,
            mx: "auto",
            lineHeight: 1.7,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function AgroforestrySection() {
  const { t } = useTranslation();

  const points = t("about.agroforestry.points", { returnObjects: true });

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: colors.white }}>
      <Container maxWidth="lg">
        <SectionHeader
          icon={<RecordVoiceOverIcon />}
          label={t("about.agroforestry.chip")}
          title={t("about.agroforestry.title")}
          subtitle={t("about.agroforestry.subtitle")}
        />
        <Grid container spacing={3}>
          {Array.isArray(points) &&
            points.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    border: "1.5px solid",
                    borderColor: alpha(colors.lightGreen, 0.25),
                    bgcolor: colors.cardBg,
                    transition: "all 0.3s ease",
                    cursor: "default",
                    "&:hover": {
                      borderColor: colors.darkGreen,
                      transform: "translateY(-6px)",
                      boxShadow: `0 12px 32px ${alpha(colors.darkGreen, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3.5 }}>
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: alpha(colors.darkGreen, 0.08),
                        mb: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          color: colors.darkGreen,
                          "& .MuiSvgIcon-root": { fontSize: 26 },
                        }}
                      >
                        {agroforestryIcons[index]}
                      </Box>
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: colors.darkText,
                        lineHeight: 1.7,
                        fontSize: "0.95rem",
                      }}
                    >
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
        <Card
          elevation={0}
          sx={{
            mt: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.mediumGreen})`,
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Avatar
              sx={{ width: 48, height: 48, bgcolor: alpha(colors.lightOrange, 0.2) }}
            >
              <EnergySavingsLeafIcon
                sx={{ color: colors.lightOrange, fontSize: 26 }}
              />
            </Avatar>
            <Typography
              sx={{
                color: colors.white,
                fontWeight: 500,
                fontSize: { xs: "0.9rem", md: "1rem" },
                lineHeight: 1.7,
                flex: 1,
              }}
            >
              {t("about.agroforestry.highlight").split("ecological").length > 1
                ? t("about.agroforestry.highlight")
                    .split("ecological")
                    .map((part, i, arr) =>
                      i < arr.length - 1 ? (
                        <React.Fragment key={i}>
                          {part}
                          <Box
                            component="strong"
                            sx={{ color: colors.lightOrange }}
                          >
                            {t("about.agroforestry.highlightEcological")}
                          </Box>
                        </React.Fragment>
                      ) : (
                        <React.Fragment key={i}>
                          {part.split("economical").map((subPart, j, subArr) =>
                            j < subArr.length - 1 ? (
                              <React.Fragment key={j}>
                                {subPart}
                                <Box
                                  component="strong"
                                  sx={{ color: colors.lightOrange }}
                                >
                                  {t("about.agroforestry.highlightEconomical")}
                                </Box>
                              </React.Fragment>
                            ) : (
                              subPart
                            )
                          )}
                        </React.Fragment>
                      )
                    )
                : t("about.agroforestry.highlight")}
            </Typography>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

function VisionSection() {
  const { t } = useTranslation();

  const tags = t("about.vision.tags", { returnObjects: true });

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(180deg, ${colors.sectionBg} 0%, ${colors.white} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          icon={<VisibilityIcon />}
          label={t("about.vision.chip")}
          title={t("about.vision.title")}
        />
        <Card
          elevation={0}
          sx={{
            maxWidth: 900,
            mx: "auto",
            borderRadius: 4,
            overflow: "hidden",
            border: "1.5px solid",
            borderColor: alpha(colors.lightOrange, 0.3),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: 160 },
                minHeight: { xs: 100, md: "auto" },
                background: `linear-gradient(180deg, ${colors.darkOrange}, ${colors.mediumOrange})`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                gap: 1.5,
              }}
            >
              <VisibilityIcon sx={{ fontSize: 48, color: colors.white }} />
              <Typography
                sx={{
                  color: colors.white,
                  fontWeight: 800,
                  fontSize: "1.1rem",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                }}
              >
                {t("about.vision.label")}
              </Typography>
              <Box
                sx={{
                  width: 40,
                  height: 3,
                  borderRadius: 2,
                  bgcolor: alpha(colors.white, 0.5),
                }}
              />
            </Box>
            <CardContent sx={{ p: { xs: 3.5, md: 5 }, flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  color: colors.darkText,
                  lineHeight: 2,
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  fontWeight: 450,
                }}
              >
                {t("about.vision.description")}{" "}
                <Chip
                  label={t("about.vision.missionChip")}
                  size="small"
                  sx={{
                    bgcolor: colors.paleGreen,
                    color: colors.darkGreen,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    verticalAlign: "middle",
                  }}
                />
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, mt: 3.5, flexWrap: "wrap" }}>
                {Array.isArray(tags) &&
                  tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      sx={{
                        borderColor: alpha(colors.darkGreen, 0.3),
                        color: colors.darkGreen,
                        fontWeight: 600,
                        fontSize: "0.78rem",
                        "&:hover": {
                          bgcolor: colors.paleGreen,
                          borderColor: colors.darkGreen,
                        },
                      }}
                    />
                  ))}
              </Box>
            </CardContent>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

function WayForwardSection() {
  const { t } = useTranslation();

  const items = t("about.wayForward.items", { returnObjects: true });

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: colors.white }}>
      <Container maxWidth="lg">
        <SectionHeader
          icon={<TrendingUpIcon />}
          label={t("about.wayForward.chip")}
          title={t("about.wayForward.title")}
          subtitle={t("about.wayForward.subtitle")}
        />
        <Grid container spacing={3}>
          {Array.isArray(items) &&
            items.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    bgcolor: colors.cardBg,
                    border: "1.5px solid",
                    borderColor: alpha(colors.lightGreen, 0.2),
                    position: "relative",
                    overflow: "visible",
                    transition: "all 0.3s ease",
                    cursor: "default",
                    "&:hover": {
                      borderColor: colors.lightOrange,
                      transform: "translateY(-4px)",
                      boxShadow: `0 10px 28px ${alpha(colors.darkOrange, 0.1)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -14,
                      left: 24,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.lightGreen})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: colors.white,
                      fontWeight: 800,
                      fontSize: "0.85rem",
                      boxShadow: `0 4px 12px ${alpha(colors.darkGreen, 0.3)}`,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <CardContent sx={{ p: 3.5, pt: 4.5 }}>
                    <Avatar
                      sx={{ width: 46, height: 46, bgcolor: colors.paleGreen, mb: 2 }}
                    >
                      <Box
                        sx={{
                          color: colors.darkGreen,
                          "& .MuiSvgIcon-root": { fontSize: 24 },
                        }}
                      >
                        {wayForwardIcons[index]}
                      </Box>
                    </Avatar>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: colors.darkGreen,
                        mb: 1,
                        fontSize: "1rem",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.subText,
                        lineHeight: 1.75,
                        fontSize: "0.9rem",
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default function About() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.white,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AgroforestrySection />
      <VisionSection />
      <WayForwardSection />
    </Box>
  );
}